/**
 * Timezone + working days + multiple non-overlapping dialing windows (AI Agent).
 */
import { useMemo, useState } from "react";
import { CaretUpDown, Check, Plus, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { COMMON_TIMEZONES, WORKING_DAY_OPTIONS } from "@/lib/campaignForm";

function Field({ label, required, hint, children, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function TimezoneCombobox({ value, onChange, timezones, testid }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => {
    const base = Array.isArray(timezones) && timezones.length ? timezones : COMMON_TIMEZONES;
    const list = [...base];
    const cur = (value || "").trim();
    if (cur && !list.includes(cur)) list.unshift(cur);
    return list;
  }, [timezones, value]);
  const display = (value || "").trim() || "Select timezone…";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3 bg-white"
        >
          <span className="truncate">{display}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__ clear timezone"
                data-testid={`${testid}-option-none`}
                onSelect={() => { onChange(""); setOpen(false); }}
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${!(value || "").trim() ? "opacity-100" : "opacity-0"}`} />
                <span className="text-muted-foreground">Account default</span>
              </CommandItem>
              {zones.map((z) => (
                <CommandItem
                  key={z}
                  value={z}
                  data-testid={`${testid}-option-${z.replace(/[^a-zA-Z0-9_-]/g, "_")}`}
                  onSelect={() => { onChange(z); setOpen(false); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${(value || "") === z ? "opacity-100" : "opacity-0"}`} />
                  {z}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function DialingScheduleFields({
  value = {},
  onChange,
  timezones = [],
  testidPrefix = "dialing-schedule",
}) {
  const v = value || {};
  const set = (patch) => onChange?.(patch);
  const windows = Array.isArray(v.dialingWindows) && v.dialingWindows.length
    ? v.dialingWindows
    : [{ start: "", end: "" }];
  const days = Array.isArray(v.workingDays) ? v.workingDays : [];

  const setWindow = (idx, patch) => {
    const next = windows.map((w, i) => (i === idx ? { ...w, ...patch } : { ...w }));
    set({
      dialingWindows: next,
      scheduleStart: next[0]?.start || "",
      scheduleEnd: next[0]?.end || "",
    });
  };

  const addWindow = () => {
    set({ dialingWindows: [...windows, { start: "", end: "" }] });
  };

  const removeWindow = (idx) => {
    const next = windows.length <= 1
      ? [{ start: "", end: "" }]
      : windows.filter((_, i) => i !== idx);
    set({
      dialingWindows: next,
      scheduleStart: next[0]?.start || "",
      scheduleEnd: next[0]?.end || "",
    });
  };

  const toggleDay = (id, checked) => {
    const next = checked
      ? (days.includes(id) ? days : [...days, id])
      : days.filter((d) => d !== id);
    set({ workingDays: next });
  };

  return (
    <div className="space-y-4" data-testid={testidPrefix}>
      <Field
        label="Timezone"
        hint="Dialing times use this timezone (or account default)."
      >
        <TimezoneCombobox
          value={v.timezone || ""}
          onChange={(x) => set({ timezone: x })}
          timezones={timezones}
          testid={`${testidPrefix}-timezone`}
        />
      </Field>

      <Field
        label="Working days"
        hint="Empty = every day. Outside these days the campaign stays active but prospecting dials pause."
      >
        <div
          className="flex flex-wrap gap-3 rounded-md border bg-white p-3"
          data-testid={`${testidPrefix}-working-days`}
        >
          {WORKING_DAY_OPTIONS.map((d) => {
            const on = days.includes(d.id);
            return (
              <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={on}
                  onCheckedChange={(x) => toggleDay(d.id, !!x)}
                  data-testid={`${testidPrefix}-day-${d.id}`}
                />
                {d.label}
              </label>
            );
          })}
        </div>
      </Field>

      <div className="space-y-3" data-testid={`${testidPrefix}-windows`}>
        <div className="flex items-center justify-between gap-2">
          <Label>Dialing times</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8"
            onClick={addWindow}
            data-testid={`${testidPrefix}-add-window`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" weight="bold" /> Add time
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Multiple windows in 24 hours are allowed; times must not overlap.
          Leave blank for 24×7. Callbacks and webhook API hits still dial when the campaign is active.
        </p>
        {windows.map((w, idx) => (
          <div
            key={`win-${idx}`}
            className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end rounded-md border bg-white p-3"
            data-testid={`${testidPrefix}-window-${idx}`}
          >
            <Field label={`Time ${idx + 1} — Dialing start (HH:MM)`}>
              <Input
                type="time"
                className="bg-white"
                value={w.start || ""}
                onChange={(e) => setWindow(idx, { start: e.target.value })}
                data-testid={`${testidPrefix}-window-${idx}-start`}
              />
            </Field>
            <Field label="Dialing end (HH:MM)">
              <Input
                type="time"
                className="bg-white"
                value={w.end || ""}
                onChange={(e) => setWindow(idx, { end: e.target.value })}
                data-testid={`${testidPrefix}-window-${idx}-end`}
              />
            </Field>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0"
              onClick={() => removeWindow(idx)}
              disabled={windows.length <= 1 && !w.start && !w.end}
              data-testid={`${testidPrefix}-window-${idx}-remove`}
              title="Remove dialing time"
            >
              <Trash className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
